import PageHeader from "@/components/page-header";
import { AccountForm } from "@/components/registrant/account-form";
import { ContextHelpButton } from "@/components/registrant/help/context-help-button";
import { InvoiceScheduleCard } from "@/components/registrant/invoice-schedule-card";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import ErrorComponent from "@/components/ui/error";
import LoadingScreen from "@/components/ui/loading-screen";
import { toast } from "@/components/ui/sonner";
import {
  generatePasswordResetUrlMutation,
  getRegistrantAccountOptions,
  updateRegistrantAccountMutation,
} from "@/hera-api/client/@tanstack/react-query.gen";
import { useAuthQuerySetup } from "@/hooks/use-auth-query-setup";
import { getQueryErrorMessage } from "@/lib/utils";
import type { AccountSchema } from "@/types/account";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import type { z } from "zod";

export const Route = createFileRoute("/registrant/account")({
  component: ManageRegistrantAccount,
});

export function ManageRegistrantAccount() {
  const { isAuthSetupComplete } = useAuthQuerySetup();
  const [isResettingPassword, setIsResettingPassword] = useState(false);

  const {
    data,
    isLoading: isQueryLoading,
    error: queryError,
    refetch,
  } = useQuery({
    ...getRegistrantAccountOptions(),
    enabled: isAuthSetupComplete,
  });

  const saveUserMutation = useMutation({
    ...updateRegistrantAccountMutation(),
  });

  const resetPasswordMutation = useMutation({
    ...generatePasswordResetUrlMutation(),
  });

  const handleSubmit = async (formData: z.infer<typeof AccountSchema>) => {
    try {
      await saveUserMutation.mutateAsync({
        body: formData,
      });
      await refetch();
      toast({
        title: "Success",
        description: "Account information updated successfully",
        variant: "secondary",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: `${getQueryErrorMessage(error)}`,
        variant: "destructive",
      });
    }
  };

  const handleResetPassword = async () => {
    try {
      setIsResettingPassword(true);
      const response = await resetPasswordMutation.mutateAsync({});
      window.location.href = response.reset_url;
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to generate password reset link",
        variant: "destructive",
      });
      setIsResettingPassword(false);
    }
  };

  if (!isAuthSetupComplete) {
    return <LoadingScreen message="Authenticating..." />;
  }
  if (isQueryLoading) {
    return <LoadingScreen message="Fetching data..." />;
  }
  if (queryError) {
    return (
      <ErrorComponent
        title={getQueryErrorMessage(queryError)}
        message={getQueryErrorMessage(queryError)}
      />
    );
  }

  return (
    <div className="flex flex-col container mx-auto max-w-3xl">
      <PageHeader
        title="Account Management"
        subtitle="Update your account information and preferences"
        className="mb-6"
        action={<ContextHelpButton articleId="account" />}
      />
      <div>
        <AccountForm initialData={data} onSubmit={handleSubmit} />

        {data ? <InvoiceScheduleCard account={data} /> : null}

        {data?.payment_account_email ? (
          <Card className="mt-6">
            <CardContent className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <h4 className="font-medium">Billing account</h4>
                <ContextHelpButton
                  articleId="billing"
                  iconOnly
                  variant="ghost"
                />
              </div>
              <p className="text-sm text-muted-foreground">
                Stripe checkout and the Customer Portal use{" "}
                {data.payment_account_name
                  ? `${data.payment_account_name} (${data.payment_account_email})`
                  : data.payment_account_email}
                . Saved cards and bank accounts stay on that member’s Stripe
                customer. Organization admins can change the billing account on
                the Users tab.
              </p>
            </CardContent>
          </Card>
        ) : null}

        <Card className="mt-6">
          <CardContent className="space-y-4">
            <h4 className="font-medium">Reset Password</h4>
            <p className="text-sm text-muted-foreground">
              If you need to change your password, click the button below. This
              will take you to a password reset page. After your password is
              reset, you will need to login into the application again with your
              new password.
            </p>
            <Button
              variant="outline"
              onClick={handleResetPassword}
              disabled={isResettingPassword}
            >
              {isResettingPassword
                ? "Redirecting..."
                : "Go to password reset page"}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
